import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Heart } from "lucide-react";

/**
 * EncouragementCard (iter66) — one-liner nudge shown at the bottom of both
 * the Simple and Advanced Progress views. Pulls the student's active
 * milestone and picks a template based on aggregate + biggest-gap axis.
 *
 * Kept purely client-side (no LLM call) so it renders instantly on page
 * load — the LLM is reserved for coach-side drafting where the extra
 * latency is acceptable.
 */
export default function EncouragementCard({ studentId, sport }) {
  const [milestone, setMilestone] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/students/${studentId}/milestones/active`, { params: { sport } })
      .then((r) => { if (!cancelled) setMilestone(r.data?.active || null); })
      .catch(() => { if (!cancelled) setMilestone(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, sport]);

  if (loading) return null;

  const { headline, body, accent } = buildEncouragement(milestone);

  return (
    <div
      className="card-surface p-6 border-l-4"
      style={{ borderLeftColor: accent }}
      data-testid="encouragement-card"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 mt-1">
          <Heart className="w-6 h-6" style={{ color: accent }} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-mono mb-1" style={{ color: accent }}>{headline}</div>
          <p className="text-base sm:text-lg leading-relaxed text-[#E5E5E5]">{body}</p>
        </div>
      </div>
    </div>
  );
}

function buildEncouragement(milestone) {
  if (!milestone) {
    return {
      headline: "YOUR NEXT STEP",
      body: "Ask your coach to set you a milestone — once they do, we'll suggest exactly what to work on next.",
      accent: "#66C2FF",
    };
  }
  const prog = milestone.progress || { aggregate: 0, biggest_gap: null };
  const agg = Math.round(prog.aggregate || 0);
  const bg = prog.biggest_gap;
  const name = milestone.name || "your milestone";

  if (!bg || bg.gap <= 0 || agg >= 100) {
    return {
      headline: "MILESTONE COMPLETE",
      body: `🏆 You did it — every target on "${name}" is hit. Book a session with your coach to lock in the win and set the next one.`,
      accent: "#CCFF00",
    };
  }

  const axis = bg.axis;
  if (agg >= 80) {
    return {
      headline: "SO CLOSE",
      body: `You're ${agg}% there! One more focused session on ${axis} will clear the "${name}" milestone.`,
      accent: "#CCFF00",
    };
  }
  if (agg >= 50) {
    return {
      headline: "GREAT PACE",
      body: `${agg}% and climbing — you're over halfway to "${name}". Ask your coach to focus this next session on ${axis}.`,
      accent: "#66C2FF",
    };
  }
  if (agg >= 20) {
    return {
      headline: "KEEP GOING",
      body: `${agg}% in. Your biggest opportunity is ${axis} — a couple of dedicated sessions there will move the needle.`,
      accent: "#66C2FF",
    };
  }
  return {
    headline: "EARLY DAYS",
    body: `Fresh start on "${name}". Book a session and ask your coach to drill ${axis} — that's the biggest lever right now.`,
    accent: "#FF4500",
  };
}
